import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import '../styles/BlogPrimitives.css';

const BLOG_POST_CANONICAL_URL = 'https://circumvent.io/blog/soc-2-type-2-attestation';
const BLOG_POST_PUBLISHED_AT = '2026-10-12';
/** Lead, headings, body, and CTA (~480 words). Read time: max(1, round(words / 200 wpm)). */
const BLOG_POST_READ_TIME = '3 min read';
const BLOG_POST_AUTHOR = 'Circumvent Team';
const SOC_BADGE_IMAGE_SRC = '/images/blog/soc2-aicpa-seal.png';
const BLOG_POST_OG_IMAGE_URL = 'https://circumvent.io/images/blog/soc2-aicpa-seal.png';
const BLOG_POST_OG_TITLE =
  'Circumvent Achieves SOC 2® Type 2 Attestation | Circumvent';
const BLOG_POST_OG_DESCRIPTION =
  'Circumvent has completed its SOC 2 Type 2 examination with a clean attestation report, confirming operating effectiveness of our security controls.';
const AICPA_SOC4SO_URL = 'https://www.aicpa.org/soc4so';

const BlogSoc2Type2Page = () => {
  return (
    <>
      <Helmet>
        <title>{BLOG_POST_OG_TITLE}</title>
        <meta name="description" content={BLOG_POST_OG_DESCRIPTION} />
        <link rel="canonical" href={BLOG_POST_CANONICAL_URL} />
      </Helmet>
      <SeoSocialTags
        title={BLOG_POST_OG_TITLE}
        description={BLOG_POST_OG_DESCRIPTION}
        url={BLOG_POST_CANONICAL_URL}
        ogType="article"
        articlePublishedTime={`${BLOG_POST_PUBLISHED_AT}T00:00:00.000Z`}
        ogImageUrl={BLOG_POST_OG_IMAGE_URL}
      />

      <main className="page page--light blog-post-page">
        <section className="page-section page-section--white" aria-labelledby="soc2-type2-post-heading">
          <article className="page-container blog-post">
            <div className="blog-post__header">
              <Link to="/blog" className="blog-crumb-link">
                <span aria-hidden="true">←</span> Blog
              </Link>
              <h1 id="soc2-type2-post-heading" className="blog-post__title">
                Circumvent Achieves SOC 2® Type 2 Attestation
              </h1>
              <p className="blog-post__lead">
                Reinforcing Our Commitment to Enterprise Security
              </p>

              <ul className="blog-meta-list blog-meta-list--header" aria-label="Post details">
                <li>Compliance update</li>
                <li>
                  <time dateTime={BLOG_POST_PUBLISHED_AT}>12 Oct 2026</time>
                </li>
                <li>{BLOG_POST_READ_TIME}</li>
                <li>By {BLOG_POST_AUTHOR}</li>
              </ul>
            </div>

            <div className="blog-post__layout">
              <div className="blog-post__content">
                <figure className="blog-post__feature-image blog-post__feature-image--seal">
                  <a
                    href={AICPA_SOC4SO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="blog-post__feature-image-link"
                  >
                    <img
                      src={SOC_BADGE_IMAGE_SRC}
                      alt="SOC 2 seal for service organizations. Opens AICPA SOC for Service Organizations page."
                      width={748}
                      height={743}
                      loading="eager"
                      decoding="async"
                    />
                  </a>
                </figure>

                <p>
                  We&apos;re thrilled to announce that Circumvent has successfully completed its System
                  and Organization Controls (SOC 2) Type 2 examination, achieving a clean attestation
                  report. This milestone represents a rigorous third-party validation of our security
                  practices and our unwavering commitment to protecting our customers&apos; most
                  sensitive data.
                </p>

                <h2 id="what-is-soc-2-type-2">What is SOC 2 Type 2?</h2>
                <p>
                  SOC 2 is an intensive auditing framework developed by the American Institute of
                  Certified Public Accountants (AICPA) that evaluates how service providers manage
                  customer data security and confidentiality. Unlike SOC 2 Type 1, which assesses the
                  design of security controls, a Type 2 attestation validates that these controls have
                  operated effectively over an extended period of time. Our examination covered the
                  audit period, during which independent auditors from Sensiba LLP verified that our
                  security controls performed consistently and reliably.
                </p>

                <h2 id="why-this-matters">Why Does This Matter for Our Customers and Partners?</h2>
                <p>
                  Security isn&apos;t just a feature at Circumvent. It&apos;s foundational to everything
                  we build. Our customers rely on us to protect their data. This SOC 2 Type 2
                  attestation provides independent, third-party assurance that we&apos;re meeting the
                  highest industry standards for data security, confidentiality, and availability.
                </p>
                <p>
                  For enterprise customers and prospects, this means you can move forward with
                  confidence that Circumvent has been thoroughly vetted by qualified, independent
                  security auditors and meets the stringent requirements expected by regulated
                  industries and risk-conscious organizations.
                </p>

                <h2 id="whats-next">What&apos;s Next?</h2>
                <p>
                  This SOC 2 Type 2 attestation is an important milestone, but our commitment to
                  security and compliance doesn&apos;t stop here.
                </p>
                <p>
                  We&apos;re focused on the continuous improvement of security and risk management
                  practices across all of Circumvent&apos;s business processes and controls.
                  This means embedding security deeper into our operations, refining our risk
                  management frameworks, and ensuring that every team across Circumvent, from product
                  and engineering to operations and customer success, plays an active role in
                  maintaining and strengthening our security posture.
                </p>
                <p>
                  A heartfelt thank you to everyone at Circumvent who contributed to achieving this
                  milestone. Your dedication to building secure systems and fostering a
                  security-conscious culture is what makes this achievement possible.
                </p>

                <div className="blog-post__cta">
                  <h3 id="access-compliance-docs">Access Our Compliance Documentation</h3>
                  <p>
                    We&apos;re committed to transparency with our customers. If you&apos;d like to
                    review our SOC 2 Type 2 report or discuss our security practices in detail, we
                    welcome you to contact us through our contact page. Our team will be happy to
                    provide access to relevant compliance documentation.
                  </p>
                  <Link to="/#contact" className="btn btn-solid">
                    Contact us
                  </Link>
                </div>

                <Link to="/blog" className="blog-crumb-link">
                  <span aria-hidden="true">←</span> Blog
                </Link>
              </div>

              <aside className="blog-post__sidebar" aria-label="Post navigation">
                <nav className="blog-toc" aria-label="On this page">
                  <p className="blog-toc__title">On this page</p>
                  <ul className="blog-toc__list">
                    <li className="blog-toc__item">
                      <a className="blog-toc__link" href="#what-is-soc-2-type-2">
                        What is SOC 2 Type 2?
                      </a>
                    </li>
                    <li className="blog-toc__item">
                      <a className="blog-toc__link" href="#why-this-matters">
                        Why Does This Matter for Our Customers and Partners?
                      </a>
                    </li>
                    <li className="blog-toc__item">
                      <a className="blog-toc__link" href="#whats-next">
                        What&apos;s Next?
                      </a>
                    </li>
                    <li className="blog-toc__item">
                      <a className="blog-toc__link" href="#access-compliance-docs">
                        Access Our Compliance Documentation
                      </a>
                    </li>
                  </ul>
                </nav>
              </aside>
            </div>
          </article>
        </section>
      </main>
    </>
  );
};

export default BlogSoc2Type2Page;
